import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import styled from "styled-components";
import usePostUser from "../../hooks/usePostUser";
import theme from "../../theme";
import { AdminCrudType, SystemSetting } from "../../types/types";
import AuthField from "./authField";
import InputField from "./inputField";
import { putAdminMemberImage } from "../../api/admin";
import AlertModal from "../alertModal";
import { WarningIcon } from "../../assets/icons/icons";
import { Button } from "../common/Button";
import { ModalLayout } from "../common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../../locales";
import {
  checkPassword,
  isValidEmail,
  isValidPhone,
} from "../../utils/util";

const { md } = theme.screen;

const UserRegisterModal: React.FC<{
  open: boolean;
  onPressNo: () => void;
  grade: number;
  systemSetting: SystemSetting | null;
}> = (props) => {
  const { open, onPressNo, grade, systemSetting } = props;
  const { width } = useWindowSize();
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [imgFile, setImgFile] = useState("");
  const [imgData, setImgData] = useState<any>();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pwCheck, setPwCheck] = useState(""); // 비밀번호 확인

  const [form, setForm] = useState<AdminCrudType>({
    address: "",
    address_detail: "",
    email: "",
    grade: grade,
    mobility_seq: "",
    name: "",
    phone: "",
    pw: "",
    id: "",
    permission: {
      auth_accident: "Y",
      auth_battery: "Y",
      auth_check: "Y",
      auth_modify: "Y",
      auth_notice: "Y",
      auth_out: "Y",
      auth_warning: "Y",
    },
    group_id: "00000",
  });

  const { mutate: postUser, isPending } = usePostUser();

  const handlePost = () => {
    postUser(
      { ...form, pw: form.pw /*? SHA256(form.pw).toString() : ""*/ },
      {
        onSuccess: async (postResponse) => {
          let imageSuccess = true; // 이미지 작업 성공 여부
          let imageMessage = ""; // 이미지 작업 메시지

          try {
            if (imgData) {
              // FormData 생성 및 이미지 업로드
              const formData = new FormData();
              formData.append("id", JSON.stringify(form?.id));
              formData.append("file", imgData);

              const response = await putAdminMemberImage(formData);

              if (response.code === 2002) {
                imageMessage = t.deviceRegister.imageUploadSuccess || "Image uploaded successfully";
              } else {
                imageSuccess = false;
                imageMessage = response.message;
              }
            }
          } catch (error) {
            imageSuccess = false;
            imageMessage = t.deviceRegister.imageUploadError || "Error occurred during image upload.";
          }

          // 메시지 처리
          enqueueSnackbar(postResponse.message, { variant: "success" });

          if (imageMessage) {
            enqueueSnackbar(imageMessage, {
              variant: imageSuccess ? "success" : "error",
            });
          }

          // 최종 정리 및 초기화
          closeToModal();
          queryClient.invalidateQueries({ queryKey: ["Member"] });
        },
      }
    );
  };

  // 모달 닫기 함수
  const closeToModal = () => {
    setOpenConfirmModal(false);
    setForm({
      address: "",
      address_detail: "",
      email: "",
      grade: grade,
      mobility_seq: "",
      name: "",
      phone: "",
      pw: "",
      id: "",
      permission: {
        auth_accident: "Y",
        auth_battery: "Y",
        auth_check: "Y",
        auth_modify: "Y",
        auth_notice: "Y",
        auth_out: "Y",
        auth_warning: "Y",
      },
      group_id: "00000",
    });
    onPressNo();
    setImgData("");
    setPwCheck("");
  };

  // 필수입력값들
  const isAllFilled =
    [form.id, form.name, form.pw, pwCheck].every((el) => el !== "") &&
    isValidPhone(form.phone) &&
    isValidEmail(form.email);

  return (
    <ModalLayout
      open={open}
      onClose={() => setOpenConfirmModal(true)}
      width="1300px"
      title={t.manageUser.register}
    >
      <InputField
        form={form}
        setForm={setForm}
        imgFile={imgFile}
        setImgFile={setImgFile}
        imgData={imgData}
        setImgData={setImgData}
        pwCheck={pwCheck}
        setPwCheck={setPwCheck}
      />
      <Title>{t.manageUser.permissionManagement}</Title>
      <AuthField form={form} setForm={setForm} />

      {/* 계정 기기 연동 
      <DeviceField form={form} setForm={setForm} />*/}
      <BtnWrapper>
        <Button
          onClick={() => handlePost()}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
          isDisabled={
            !isAllFilled ||
            checkPassword({ pw: form.pw, pwCheck }).pwError ||
            checkPassword({ pw: form.pw, pwCheck }).pwCheckError ||
            !systemSetting?.create
          }
          isLoading={isPending}
        >
          {width <= md ? t.common.save : t.manageUser.register}
        </Button>
        <Button
          bgColor={theme.statusColor.CLOSE}
          onClick={() => setOpenConfirmModal(true)}
          hoverColor={theme.statusColor.CLOSE_HOVER}
        >
          {t.common.cancel}
        </Button>
      </BtnWrapper>
      <AlertModal
        open={openConfirmModal}
        onPressNo={() => setOpenConfirmModal(false)}
        title={t.manageUser.cancelRegisterTitle}
        content={t.manageUser.cancelRegisterContent}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
        onPressYes={closeToModal}
      />
    </ModalLayout>
  );
};

const Title = styled.div`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_500};
  padding-bottom: 20px;
  padding-left: 0px;
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;
  }
`;

export default UserRegisterModal;
